import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { Network } from "vis-network/standalone";

export interface HoverableNode {
	name: string;
	elementType: string;
	metadata: Record<string, unknown>;
	hint?: string;
}

/** Internal vis-network hoverNode event shape. */
interface VisHoverParams {
	node: string;
	pointer: { DOM: { x: number; y: number } };
}

interface UseNodeHoverTooltipOptions {
	containerRef: React.RefObject<HTMLDivElement | null>;
	/** Return node display data for the given vis-network node id, or null to suppress the tooltip. */
	getNodeData: (nodeId: string) => HoverableNode | null;
	debounceMs?: number;
}

interface UseNodeHoverTooltipResult {
	/** Call immediately after creating a Network instance to wire up hover events. */
	registerHoverEvents: (network: Network) => void;
	/** Call in useEffect cleanup (and blurNode) to reset state and clear any pending timer. */
	clearHover: () => void;
	hoveredNode: HoverableNode | null;
	tooltipPos: { x: number; y: number } | null;
}

/**
 * Manages the 300 ms debounced node hover tooltip state for a vis-network instance.
 * Consumers provide `getNodeData` to map a vis-network node id → display data.
 */
export function useNodeHoverTooltip({
	containerRef,
	getNodeData,
	debounceMs = 300,
}: UseNodeHoverTooltipOptions): UseNodeHoverTooltipResult {
	const [hoveredNode, setHoveredNode] = useState<HoverableNode | null>(null);
	const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
	const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Keep a ref so the hoverNode closure always reads the latest getNodeData
	// without requiring network event re-registration when filteredNodes etc. change.
	const getNodeDataRef = useRef(getNodeData);
	useEffect(() => {
		getNodeDataRef.current = getNodeData;
	});

	const clearHover = useCallback(() => {
		if (hoverTimerRef.current) {
			clearTimeout(hoverTimerRef.current);
			hoverTimerRef.current = null;
		}
		setHoveredNode(null);
		setTooltipPos(null);
	}, []);

	const registerHoverEvents = useCallback(
		(network: Network) => {
			network.on("hoverNode", (params: VisHoverParams) => {
				const { x, y } = params.pointer.DOM;
				const rect = containerRef.current?.getBoundingClientRect();
				if (!rect) return;
				const absPos = { x: rect.left + x, y: rect.top + y };
				if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
				hoverTimerRef.current = setTimeout(() => {
					const nodeData = getNodeDataRef.current(params.node);
					if (!nodeData) return;
					setHoveredNode(nodeData);
					setTooltipPos(absPos);
				}, debounceMs);
			});
			network.on("blurNode", clearHover);
		},
		[containerRef, debounceMs, clearHover],
	);

	return { registerHoverEvents, clearHover, hoveredNode, tooltipPos };
}
